// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { ActionUnion, createAction, ThunkAction } from 'utils/redux';
import { ActiveInference, ModelsQuery, ActiveInferenceRef } from 'reducers';
import { getCore, MLModel, RQStatus } from 'cvat-core-wrapper';
import { filterNull } from 'utils/filter-null';

export enum ModelsActionTypes {
    GET_MODELS = 'GET_MODELS',
    GET_MODELS_SUCCESS = 'GET_MODELS_SUCCESS',
    GET_MODELS_FAILED = 'GET_MODELS_FAILED',
    CREATE_MODEL = 'CREATE_MODEL',
    CREATE_MODEL_SUCCESS = 'CREATE_MODEL_SUCCESS',
    CREATE_MODEL_FAILED = 'CREATE_MODEL_FAILED',
    DELETE_MODEL = 'DELETE_MODEL',
    DELETE_MODEL_SUCCESS = 'DELETE_MODEL_SUCCESS',
    DELETE_MODEL_FAILED = 'DELETE_MODEL_FAILED',
    START_INFERENCE_FAILED = 'START_INFERENCE_FAILED',
    GET_INFERENCE_STATUS_SUCCESS = 'GET_INFERENCE_STATUS_SUCCESS',
    GET_INFERENCE_STATUS_FAILED = 'GET_INFERENCE_STATUS_FAILED',
    GET_INFERENCE_STATUS_SUCCESS_REF = 'GET_INFERENCE_STATUS_SUCCESS_REF',
    GET_INFERENCE_STATUS_FAILED_REF = 'GET_INFERENCE_STATUS_FAILED_REF',
    FETCH_META_FAILED = 'FETCH_META_FAILED',
    SHOW_RUN_MODEL_DIALOG = 'SHOW_RUN_MODEL_DIALOG',
    SHOW_RUN_MODEL_DIALOG_REF = 'SHOW_RUN_MODEL_DIALOG_REF',
    CLOSE_RUN_MODEL_DIALOG = 'CLOSE_RUN_MODEL_DIALOG',
    CLOSE_RUN_MODEL_DIALOG_REF = 'CLOSE_RUN_MODEL_DIALOG_REF',
    CANCEL_INFERENCE_SUCCESS = 'CANCEL_INFERENCE_SUCCESS',
    CANCEL_INFERENCE_SUCCESS_REF = 'CANCEL_INFERENCE_SUCCESS_REF',
    CANCEL_INFERENCE_FAILED = 'CANCEL_INFERENCE_FAILED',
    CANCEL_INFERENCE_FAILED_REF = 'CANCEL_INFERENCE_FAILED_REF',
    GET_MODEL_PROVIDERS = 'GET_MODEL_PROVIDERS',
    GET_MODEL_PROVIDERS_SUCCESS = 'GET_MODEL_PROVIDERS_SUCCESS',
    GET_MODEL_PROVIDERS_FAILED = 'GET_MODEL_PROVIDERS_FAILED',
    GET_MODEL_PREVIEW = 'GET_MODEL_PREVIEW',
    GET_MODEL_PREVIEW_SUCCESS = 'GET_MODEL_PREVIEW_SUCCESS',
    GET_MODEL_PREVIEW_FAILED = 'GET_MODEL_PREVIEW_FAILED',
}

export const modelsActions = {
    getModels: (query?: ModelsQuery) => createAction(ModelsActionTypes.GET_MODELS, { query }),
    getModelsSuccess: (models: MLModel[], count: number) => createAction(ModelsActionTypes.GET_MODELS_SUCCESS, {
        models,
        count,
    }),
    getModelsFailed: (error: any) => createAction(ModelsActionTypes.GET_MODELS_FAILED, {
        error,
    }),
    fetchMetaFailed: (error: any) => createAction(ModelsActionTypes.FETCH_META_FAILED, { error }),
    getInferenceStatusSuccess: (taskID: number, activeInference: ActiveInference) => createAction(ModelsActionTypes.GET_INFERENCE_STATUS_SUCCESS, {
        taskID,
        activeInference,
    }),
    getInferenceStatusFailed: (taskID: number, activeInference: ActiveInference, error: any) => createAction(ModelsActionTypes.GET_INFERENCE_STATUS_FAILED, {
        taskID,
        activeInference,
        error,
    }),
    getInferenceStatusSuccessRef: (taskID: number, activeInference: ActiveInferenceRef) => createAction(ModelsActionTypes.GET_INFERENCE_STATUS_SUCCESS_REF, {
        taskID,
        activeInference,
    }),
    getInferenceStatusFailedRef: (taskID: number, activeInference: ActiveInferenceRef, error: any) => createAction(ModelsActionTypes.GET_INFERENCE_STATUS_FAILED_REF, {
        taskID,
        activeInference,
        error,
    }),
    startInferenceFailed: (taskID: number, error: any) => createAction(ModelsActionTypes.START_INFERENCE_FAILED, {
        taskID,
        error,
    }),
    cancelInferenceSuccess: (taskID: number) => createAction(ModelsActionTypes.CANCEL_INFERENCE_SUCCESS, {
        taskID,
    }),
    cancelInferenceSuccessRef: (taskID: number) => createAction(ModelsActionTypes.CANCEL_INFERENCE_SUCCESS_REF, {
        taskID,
    }),
    cancelInferenceFailed: (taskID: number, error: any) => createAction(ModelsActionTypes.CANCEL_INFERENCE_FAILED, {
        taskID,
        error,
    }),
    cancelInferenceFailedRef: (taskID: number, error: any) => createAction(ModelsActionTypes.CANCEL_INFERENCE_FAILED_REF, {
        taskID,
        error,
    }),
    closeRunModelDialog: () => createAction(ModelsActionTypes.CLOSE_RUN_MODEL_DIALOG),
    closeRunModelDialogRef: () => createAction(ModelsActionTypes.CLOSE_RUN_MODEL_DIALOG_REF),
    showRunModelDialog: (taskInstance: any) => createAction(ModelsActionTypes.SHOW_RUN_MODEL_DIALOG, {
        taskInstance,
    }),
    showRunModelDialogRef: (referenceInstance: any) => createAction(ModelsActionTypes.SHOW_RUN_MODEL_DIALOG_REF, {
        referenceInstance,
    }),
    getModelPreview: (modelID: string | number) => createAction(ModelsActionTypes.GET_MODEL_PREVIEW, { modelID }),
    getModelPreviewSuccess: (modelID: string | number, preview: string) => createAction(ModelsActionTypes.GET_MODEL_PREVIEW_SUCCESS, { modelID, preview }),
    getModelPreviewFailed: (modelID: string | number, error: any) => createAction(ModelsActionTypes.GET_MODEL_PREVIEW_FAILED, { modelID, error }),
};

export type ModelsActions = ActionUnion<typeof modelsActions>;

const core = getCore();

export function getModelsAsync(query?: ModelsQuery): ThunkAction {
    return async (dispatch, getState): Promise<void> => {
        dispatch(modelsActions.getModels(query));

        const filteredQuery = filterNull(query || getState().models.query);
        try {
            // @ts-ignore
            const result = await core.lambda.list(filteredQuery);
            const { models, count } = result;
            dispatch(modelsActions.getModelsSuccess(models, count));
        } catch (error) {
            dispatch(modelsActions.getModelsFailed(error));
        }
    };
}

interface InferenceMeta {
    taskID: number;
    requestID: string;
    functionID: string | number;
}

function listen(inferenceMeta: InferenceMeta, dispatch: (action: ModelsActions) => void): void {
    const { taskID, requestID, functionID } = inferenceMeta;
    core.lambda
        .listen(requestID, functionID as string, (status: RQStatus, progress: number, message: string) => {
            if (status === RQStatus.FAILED || status === RQStatus.UNKNOWN) {
                dispatch(
                    modelsActions.getInferenceStatusFailed(
                        taskID,
                        {
                            status,
                            progress,
                            functionID,
                            error: message,
                            id: requestID,
                        },
                        new Error(`Inference status for the task ${taskID} is ${status}. ${message}`),
                    ),
                );

                return;
            }

            dispatch(
                modelsActions.getInferenceStatusSuccess(taskID, {
                    status,
                    progress,
                    functionID,
                    error: message,
                    id: requestID,
                }),
            );
        })
        .catch((error: Error) => {
            dispatch(
                modelsActions.getInferenceStatusFailed(
                    taskID,
                    {
                        status: RQStatus.UNKNOWN,
                        progress: 0,
                        error: error.toString(),
                        id: requestID,
                        functionID,
                    },
                    error,
                ),
            );
        });
}

export function getInferenceStatusAsync(): ThunkAction {
    return async (dispatch): Promise<void> => {
        const dispatchCallback = (action: ModelsActions): void => {
            dispatch(action);
        };

        try {
            const requests = await core.lambda.requests();
            requests.forEach((request: any): void => {
                // a run on a reference names the reference, not a task: its id must not land on a task
                const reference = request.function.reference;
                const meta: InferenceMeta = {
                    taskID: +(reference ?? request.function.task),
                    requestID: request.id,
                    functionID: request.function.id,
                };
                if (reference) listenRef(meta, dispatchCallback);
                else listen(meta, dispatchCallback);
            });
        } catch (error) {
            dispatch(modelsActions.fetchMetaFailed(error));
        }
    };
}

export function startInferenceAsync(taskId: number, model: MLModel, body: object): ThunkAction {
    return async (dispatch): Promise<void> => {
        try {
            const requestID: string = await core.lambda.run(taskId, model, body);
            const dispatchCallback = (action: ModelsActions): void => {
                dispatch(action);
            };

            listen(
                {
                    taskID: taskId,
                    functionID: model.id,
                    requestID,
                },
                dispatchCallback,
            );
        } catch (error) {
            dispatch(modelsActions.startInferenceFailed(taskId, error));
        }
    };
}

function listenRef(inferenceMeta: InferenceMeta, dispatch: (action: ModelsActions) => void): void {
    const { taskID, requestID, functionID } = inferenceMeta;
    core.lambda
        .listen(requestID, functionID as string, (status: RQStatus, progress: number, message: string) => {
            if (status === RQStatus.FAILED || status === RQStatus.UNKNOWN) {
                dispatch(
                    modelsActions.getInferenceStatusFailedRef(
                        taskID,
                        {
                            status,
                            progress,
                            functionID,
                            error: message,
                            id: requestID,
                        },
                        new Error(`Inference status for the reference ${taskID} is ${status}. ${message}`),
                    ),
                );

                return;
            }

            dispatch(
                modelsActions.getInferenceStatusSuccessRef(taskID, {
                    status,
                    progress,
                    functionID,
                    error: message,
                    id: requestID,
                }),
            );
        })
        .catch((error: Error) => {
            dispatch(
                modelsActions.getInferenceStatusFailed(
                    taskID,
                    {
                        status: RQStatus.UNKNOWN,
                        progress: 0,
                        error: error.toString(),
                        id: requestID,
                        functionID,
                    },
                    error,
                ),
            );
        });
}

export function startInferenceAsyncRef(taskId: number, model: MLModel, body: object): ThunkAction {
    return async (dispatch): Promise<void> => {
        try {
            const requestID: string = await core.lambda.run(taskId, model, body, 'reference');
            const dispatchCallback = (action: ModelsActions): void => {
                dispatch(action);
            };

            listenRef(
                {
                    taskID: taskId,
                    functionID: model.id,
                    requestID,
                },
                dispatchCallback,
            );
        } catch (error) {
            dispatch(modelsActions.startInferenceFailed(taskId, error));
        }
    };
}

export function cancelInferenceAsync(taskID: number): ThunkAction {
    return async (dispatch, getState): Promise<void> => {
        try {
            const inference = getState().models.inferences[taskID];
            await core.lambda.cancel(inference.id, inference.functionID);
            dispatch(modelsActions.cancelInferenceSuccessRef(taskID));
        } catch (error) {
            dispatch(modelsActions.cancelInferenceFailed(taskID, error));
        }
    };
}

export function cancelInferenceAsyncRef(taskID: number): ThunkAction {
    return async (dispatch, getState): Promise<void> => {
        try {
            const inference = getState().models.inferencesRef[taskID];
            await core.lambda.cancel(inference.id, inference.functionID);
            dispatch(modelsActions.cancelInferenceSuccess(taskID));
        } catch (error) {
            dispatch(modelsActions.cancelInferenceFailed(taskID, error));
        }
    };
}

export const getModelPreviewAsync =
    (model: MLModel): ThunkAction => async (dispatch) => {
        dispatch(modelsActions.getModelPreview(model.id));
        try {
            const result = await model.preview();
            dispatch(modelsActions.getModelPreviewSuccess(model.id, result));
        } catch (error) {
            dispatch(modelsActions.getModelPreviewFailed(model.id, error));
        }
    };
